import { formatBirthdayForData } from '@/modules/localization';

/** Outcome of validating a survey date answer. */
export type SurveyDateValidation = {
  /** True when `normalizedDate` is a real date on or after `min`. */
  isValid: boolean;
  /**
   * Inclusive lower bound as a local-calendar ISO `YYYY-MM-DD` string: Jan 1 of
   * *last* year. A valid date must fall on or after this; there is no upper
   * bound. Exposed so the field can name the floor in its helper/error copy.
   */
  min: string;
  /**
   * The answer normalized to a local-calendar ISO `YYYY-MM-DD` string (ready to
   * commit), or `null` when the input can't be parsed (incomplete / not a real
   * calendar date).
   */
  normalizedDate: string | null;
};

/**
 * Validates a survey date answer. Reuses the shared MM/DD/YYYY
 * mask/parse helper (`formatBirthdayForData`) — despite the name it's a generic
 * date parser — to normalize the input, then requires the result to fall on or
 * after `min` (Jan 1 of last year). `formatBirthdayForData` only gates
 * format/calendar validity; the `min` comparison is what rejects an implausibly
 * old date (e.g. `09 / 09 / 1000`).
 */
export function validateSurveyDate(input?: string | null): SurveyDateValidation {
  // Jan 1 of last year, as a local-calendar ISO string. Month/day are literal,
  // so no formatting helper is needed.
  const min = `${new Date().getFullYear() - 1}-01-01`;

  const normalizedDate = formatBirthdayForData(input) || null;

  // Zero-padded ISO `YYYY-MM-DD` strings compare lexicographically the same as
  // chronologically, so bound the normalized string directly — no Date parsing
  // and no UTC/local skew.
  const isValid = normalizedDate !== null && normalizedDate >= min;
  return { isValid, min, normalizedDate };
}
